import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Card,
  CardContent,
  Grid,
  Tooltip,
  TableFooter,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  TablePagination,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from "@mui/material";
import {
  Search,
  Close,
  Person,
  Payment,
  Receipt,
  AccountBalance,
  Warning,
  ArrowForward,
  Visibility,
  CheckCircle,
  AttachMoney,
  Description,
  AttachFile,
  ZoomIn,
  FilterList,
  Download,
  ExpandMore,
  ExpandLess,
  Refresh,
} from "@mui/icons-material";
import { formatCurrencyN } from "../../../common/utils_t";
import {
  getAllCustomers2,
  getCustomersWithPendingTransactions,
} from "../../../api/customers/customers";
import { IonApp, IonButton, IonContent, IonHeader, IonPage, IonToolbar } from "@ionic/react";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { fetchSalesData_withdate_staffId_paymentMethodCall, getManagersOrders_reciept, getManagersOrders_reciept_withdate, getOrderTransaction, verifyTransaction } from "../../../api/reports/reports";
import { getStaffList_saleapp } from "../../../api/users/user";
import { baseUrl } from "../../../common/utils";
import { MenuButton } from "../../../components/Menu";

interface Customer {
  id: string;
  user_id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  grade_letter: string;
}

interface PendingTransactionSummary {
  customer_id: string;
  total_outstanding: number;
  transaction_count: number;
  customer: Customer;
  member_name: string;
  customer_phone: string;
  customer_email: string;
  balance_due: number;
  // ADD TRANSACTION FIELDS HERE
  order_id?: string;
  order_code?: string;
  order_date?: string;
  mode_of_payment?: string;
  payable_amount?: string;
  amount_paid?: string;
  order_status?: string;
  product_id_type?: string;
  is_verified?: number;
  is_audited?: number;
  // Additional fields for verification dialog
  customer_name?: string;
  customer_account_name?: string;
  customer_account_number?: string;
  bank_name?: string;
  active_status?: string;
  staff_name?: string;
  mode_of_payment_real?: string;
  card_amount?: string;
  rmbValue?: string;
  cryptoType?: string;
  cryptoAmount?: string;
  costPrice?: string;
  sellingPrice?: string;
  commission?: string;
  external_processor?: string;
  processor_type?: string;
  processor_category?: string;
  load_type?: string;
  card_rate?: string;
  naira_rate?: string;
  internal_rate?: string;
  created_at?: string;
  last_updated?: string;
  proof_of_payment?: string;
  proof_of_jobstart?: string;
  crypto_value?: string;
  images?: string[];
  screenshot?: string;
  transaction_proof?: string;
}

interface OrderItem {
  product_id: string;
  quantity: string;
  product_name: string;
  price: string;
  total_amount: string;
  item_status: string;
}

interface SalesStats {
  total_sales: number;
  total_orders: number;
  average_order_value: number;
  top_payment_method: string;
  growth_percentage: number;
  pending_orders: number;
  completed_orders: number;
}

interface Staff {
  staff_id: string;
  staff_name: string;
  user_id: string;
  firstName: string;
  lastName: string;
}

interface FilterState {
  startDate: Date | null;
  endDate: Date | null;
  paymentMethod: string;
  department: string;
  orderStatus: string;
  paymentStatus: string;
  staffId: string;
  productType: string;
}

const CustomerAccountPage_giftcard_crypto_pending_om: React.FC = () => {
  const history = useHistory();
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [pendingTransactions, setPendingTransactions] = useState<PendingTransactionSummary[]>([]);
  const [filteredPending, setFilteredPending] = useState<PendingTransactionSummary[]>([]);
  const [loading, setLoading] = useState({
    customers: false,
    pending: false,
  });
  const [searchQuery, setSearchQuery] = useState("");

  // NEW STATE VARIABLES FOR DIALOG AND FILTERS
  const [showFilters, setShowFilters] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [transactionToVerify, setTransactionToVerify] = useState<PendingTransactionSummary | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyDialogLoading, setVerifyDialogLoading] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  // NEW STATE FOR AUTO REFRESH
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date>(new Date());
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [refreshCount, setRefreshCount] = useState(0);

  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [filters, setFilters] = useState<FilterState>({
    startDate: dayjs().toDate(),
    endDate: dayjs().toDate(),
    paymentMethod: "",
    department: "",
    orderStatus: "",
    paymentStatus: "",
    staffId: "",
    productType: "",
  });

  const paymentMethods = ["All", "manual_transfer", "auto_transfer"];
  const orderStatuses = ["All", "COMPLETED", "PENDING", "CANCELLED"];
  const paymentStatuses = ["All", "0", "1", "2"];

  // Use refs to prevent stale closures in intervals
  const autoRefreshEnabledRef = useRef(autoRefreshEnabled);
  const verifyDialogOpenRef = useRef(verifyDialogOpen);
  
  // Update refs when state changes
  useEffect(() => {
    autoRefreshEnabledRef.current = autoRefreshEnabled;
    verifyDialogOpenRef.current = verifyDialogOpen;
  }, [autoRefreshEnabled, verifyDialogOpen]);

  // Fetch all data on component mount
  useEffect(() => {
    fetchAllCustomers();
    fetchPendingTransactions();
    fetchStaffData();
    
    // Set up auto-refresh
    const intervalId = setInterval(() => {
      // Only refresh if auto-refresh is enabled AND no dialog is open
      if (autoRefreshEnabledRef.current && !verifyDialogOpenRef.current) {
        fetchPendingTransactionsQuietly();
      }
    }, 8000); // 8 seconds

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  // Apply filters when search changes
  useEffect(() => {
    applyFilters();
  }, [searchQuery, filters, pendingTransactions]);

  // Fetch all customers
  const fetchAllCustomers = async () => {
    try {
      setLoading((prev) => ({ ...prev, customers: true }));
      const response = await getAllCustomers2();
      setAllCustomers(response.data);
    } catch (error) {
      console.error("Error fetching all customers:", error);
    } finally {
      setLoading((prev) => ({ ...prev, customers: false }));
    }
  };

  // Main fetch function - for manual calls
  const fetchPendingTransactions = async () => {
    try {
      setLoading((prev) => ({ ...prev, pending: true }));
      const response = await getCustomersWithPendingTransactions();
      console.log("Fetched pending transactions:", response.data);
      
      // Transform the data to include transaction details
      const transformedData = response.data.orders.map((item: any) => ({
        ...item,
        // Map transaction fields if they exist in the response
        order_id: item.order_id || item.transaction_id || "",
        order_code: item.order_code || "N/A",
        order_date: item.order_date || item.created_at || "",
        mode_of_payment: item.mode_of_payment || "N/A",
        payable_amount: item.payable_amount || "0",
        amount_paid: item.amount_paid || "0",
        order_status: item.order_status || "PENDING",
        product_id_type: item.product_id_type || "N/A",
        is_verified: item.is_verified || 0,
        is_audited: item.is_audited || 0,
        // Map customer fields
        customer_name: item.member_name || "",
        customer_phone: item.customer_phone || "",
        customer_email: item.customer_email || "",
      }));
      
      setPendingTransactions(transformedData);
      setFilteredPending(transformedData);
      setLastRefreshTime(new Date());
    } catch (error) {
      console.error("Error fetching pending transactions:", error);
    } finally {
      setLoading((prev) => ({ ...prev, pending: false }));
    }
  };

  // Quiet fetch function - for auto-refresh (no loading indicators)
  const fetchPendingTransactionsQuietly = async () => {
    // Skip if already refreshing or dialog is open
    if (isAutoRefreshing || verifyDialogOpenRef.current) return;
    
    try {
      setIsAutoRefreshing(true);
      const response = await getCustomersWithPendingTransactions();
      console.log("Auto-refreshed pending transactions:", response.data);
      
      // Transform the data to include transaction details
      const transformedData = response.data.orders.map((item: any) => ({
        ...item,
        // Map transaction fields if they exist in the response
        order_id: item.order_id || item.transaction_id || "",
        order_code: item.order_code || "N/A",
        order_date: item.order_date || item.created_at || "",
        mode_of_payment: item.mode_of_payment || "N/A",
        payable_amount: item.payable_amount || "0",
        amount_paid: item.amount_paid || "0",
        order_status: item.order_status || "PENDING",
        product_id_type: item.product_id_type || "N/A",
        is_verified: item.is_verified || 0,
        is_audited: item.is_audited || 0,
        // Map customer fields
        customer_name: item.member_name || "",
        customer_phone: item.customer_phone || "",
        customer_email: item.customer_email || "",
      }));
      
      setPendingTransactions(transformedData);
      setFilteredPending(transformedData);
      setLastRefreshTime(new Date());
      setRefreshCount(prev => prev + 1);
    } catch (error) {
      console.error("Error during auto-refresh:", error);
    } finally {
      setIsAutoRefreshing(false);
    }
  };

  // Fetch staff data
  const fetchStaffData = async () => {
    try {
      const response = await getStaffList_saleapp();
      console.log({ staffResponse: response });
      if (response.data) {
        setStaffList(response.data);
      }
    } catch (error) {
      console.error("Error fetching staff data:", error);
    }
  };

  // Filter transactions
  const applyFilters = () => {
    let filtered = pendingTransactions || [];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered?.filter(
        (transaction) =>
          transaction?.order_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transaction?.member_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transaction?.customer_phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transaction?.customer_email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filters.startDate && filters.endDate) {
      const start = new Date(filters.startDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);

      filtered = filtered?.filter((transaction) => {
        if (!transaction?.order_date) return false;
        const transactionDate = new Date(transaction.order_date);
        return transactionDate >= start && transactionDate <= end;
      });
    }

    // Apply payment method filter
    if (filters.paymentMethod && filters.paymentMethod !== "All") {
      filtered = filtered?.filter(
        (transaction) => transaction?.mode_of_payment === filters.paymentMethod
      );
    }

    // Apply order status filter
    if (filters.orderStatus && filters.orderStatus !== "All") {
      filtered = filtered?.filter((transaction) => transaction?.order_status === filters.orderStatus);
    }

    // Apply staff filter
    if (filters.staffId && filters.staffId !== "All") {
      filtered = filtered?.filter((transaction) => transaction?.order_by === filters.staffId);
    }

    // Apply product type filter
    if (filters.productType && filters.productType !== "All") {
      filtered = filtered?.filter((transaction) => transaction?.product_id_type === filters.productType);
    }

    setFilteredPending(filtered);
  };

  const handleFilterChange = (field: keyof FilterState, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      startDate: dayjs().toDate(),
      endDate: dayjs().toDate(),
      paymentMethod: "",
      department: "",
      orderStatus: "",
      paymentStatus: "",
      staffId: "",
      productType: "",
    });
    setSearchQuery("");
  };

  // Toggle auto-refresh
  const toggleAutoRefresh = () => {
    setAutoRefreshEnabled(prev => !prev);
  };

  // Manual refresh function
  const handleManualRefresh = () => {
    fetchPendingTransactions();
  };

  // Verification dialog functions
  const handleVerifyClick = async (transaction: PendingTransactionSummary) => {
    setVerifyDialogOpen(true);
    setVerifyDialogLoading(true);

    try {
      // First, get full transaction details
      const transactionToFetch = {
        order_code: transaction.order_code,
        order_id: transaction.order_id,
        member_id: transaction.customer_id,
      };
      
      const response = await getOrderTransaction(transactionToFetch);
      console.log("Transaction details:", response.data);

      if (response.status) {
        // Merge the fetched details with the existing transaction
        setTransactionToVerify({
          ...transaction,
          ...response.data
        });
      } else {
        // If API fails, use the basic transaction data
        setTransactionToVerify(transaction);
      }
    } catch (error) {
      console.error("Error fetching transaction details:", error);
      // Use the basic transaction data if API fails
      setTransactionToVerify(transaction);
    } finally {
      setTimeout(() => {
        setVerifyDialogLoading(false);
      }, 500);
    }
  };

  const handleVerifyTransaction = async () => {
    if (!transactionToVerify) return;
    console.log(transactionToVerify.order_code);
    setIsVerifying(true);
    try {
      const response = await verifyTransaction(transactionToVerify);
      // Refresh the data after verification
      fetchPendingTransactions();
    } catch (error) {
      console.error("Error verifying transaction:", error);
      alert("An error occurred during verification.");
    } finally {
      setIsVerifying(false);
      setVerifyDialogOpen(false);
      setTransactionToVerify(null);
    }
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setImageDialogOpen(true);
  };

  const getOrderStatusColor = (status: string) => {
    if (!status) return "default";
    switch (status.toUpperCase()) {
      case "COMPLETED":
        return "success";
      case "PENDING":
        return "warning";
      case "CANCELLED":
        return "error";
      default:
        return "default";
    }
  };

  const getPaymentStatusLabel = (status: string) => {
    switch (status) {
      case "0":
        return "Unpaid";
      case "1":
        return "Paid";
      case "2":
        return "Partial";
      default:
        return "Unknown";
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case "0":
        return "error";
      case "1":
        return "success";
      case "2":
        return "warning";
      default:
        return "default";
    }
  };

  const formatCurrency = (amount: string | number) => {
    const numAmount = typeof amount === "string" ? Number.parseFloat(amount) : amount;
    if (isNaN(numAmount)) return "₦0.00";
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(numAmount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Invalid Date";
    try {
      return new Date(dateString).toLocaleDateString("en-NG", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    
    if (diffSec < 60) return `${diffSec} seconds ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} minutes ago`;
    return `${Math.floor(diffSec / 3600)} hours ago`;
  };

  const handleCustomerSelect = (selectedCustomer: PendingTransactionSummary) => {
    const selectedCustomerx: Customer = {
      id: selectedCustomer.member_id,
      user_id: selectedCustomer.member_id,
      firstName: selectedCustomer.member_name,
      phone: selectedCustomer.customer_phone,
      email: selectedCustomer.customer_email,
      grade_letter: "A",
    };
    console.log("Selected customer:", selectedCustomerx);

    history.push("/folder/raise-receipt_giftcard_crypto", { customer: selectedCustomerx });
  };

  const handleViewAllCustomers = () => {
    history.push("/folder/customer-accounts-giftcard-crypto");
  };

  const getOutstandingColor = (amount: number) => {
    if (amount > 50000) return "error";
    if (amount > 20000) return "warning";
    return "primary";
  };

  // Calculate totals for pending transactions
  const totalOutstanding = filteredPending.reduce(
    (sum, item) => sum + Number(item.balance_due),
    0
  );
  const totalCustomers = filteredPending.length;

  return (
    <IonPage className="pushDown">
              <MenuButton/>
    
      <IonContent>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h4" gutterBottom>
              Pending Transactions - GiftCard & Crypto
            </Typography>
            
            {/* Auto-refresh indicator and controls */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {autoRefreshEnabled ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CircularProgress size={12} />
                    <span>Auto-refresh enabled</span>
                  </Box>
                ) : (
                  "Auto-refresh paused"
                )}
              </Typography>
              
              <Tooltip title={autoRefreshEnabled ? "Pause auto-refresh" : "Enable auto-refresh"}>
                <IconButton 
                  size="small" 
                  onClick={toggleAutoRefresh}
                  color={autoRefreshEnabled ? "primary" : "default"}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Refresh now">
                <IconButton 
                  size="small" 
                  onClick={handleManualRefresh}
                  disabled={loading.pending}
                >
                  {loading.pending ? <CircularProgress size={20} /> : <Refresh />}
                </IconButton>
              </Tooltip>
              
              <Typography variant="caption" color="text.secondary">
                Last updated: {formatTimeAgo(lastRefreshTime)}
              </Typography>
            </Box>
          </Box>

          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: "warning.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Warning sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6">Total Outstanding</Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {formatCurrencyN(totalOutstanding)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: "success.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Person sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6">Customers with Pending</Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {totalCustomers}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: "info.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Refresh sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6">Auto-refresh Status</Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {autoRefreshEnabled ? "Active" : "Paused"}
                  </Typography>
                  <Typography variant="caption">
                    Refreshes every 8 seconds
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search and Actions */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
              <TextField
                placeholder="Search by order code, customer name, phone, or email..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 300 }}
              />

              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filters
              </Button>

              <Box sx={{ flexGrow: 1 }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {isAutoRefreshing && (
                  <CircularProgress size={16} />
                )}
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredPending.length} of {pendingTransactions.length} transactions
                  {refreshCount > 0 && ` • Auto-refreshed ${refreshCount} times`}
                </Typography>
              </Box>
            </Box>

            {/* Filter Panel */}
            {showFilters && (
              <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="Start Date"
                      value={dayjs(filters.startDate)}
                      onChange={(date) => {
                        const dateValue = date ? date.toDate() : null;
                        handleFilterChange("startDate", dateValue);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="End Date"
                      value={dayjs(filters.endDate)}
                      onChange={(date) => {
                        const dateValue = date ? date.toDate() : null;
                        handleFilterChange("endDate", dateValue);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Product Type</InputLabel>
                      <Select
                        value={filters.productType}
                        label="Product Type"
                        onChange={(e) => {
                          handleFilterChange("productType", e.target.value);
                        }}
                      >
                        <MenuItem value="">All Types</MenuItem>
                        <MenuItem value="GiftCard">Gift Card</MenuItem>
                        <MenuItem value="Crypto">Crypto</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={clearFilters}
                      fullWidth
                    >
                      Clear Filters
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>

          {/* Results Summary */}
          <Alert 
            severity="info" 
            sx={{ 
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}
          >
            <Box>
              Showing {filteredPending.length} of {pendingTransactions.length} pending transactions
              {filters.productType && filters.productType !== "All" && (
                <span> • Filtered by: {filters.productType}</span>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress 
                size={16} 
                thickness={autoRefreshEnabled ? 4 : 2} 
                color={autoRefreshEnabled ? "primary" : "inherit"}
              />
              <Typography variant="caption">
                Auto-refresh: {autoRefreshEnabled ? "ON" : "OFF"}
              </Typography>
            </Box>
          </Alert>

          {/* SINGLE TABLE WITH CUSTOMER AND TRANSACTION DETAILS */}
          <TableContainer
            component={Paper}
            sx={{
              overflowX: "auto",
              "&::-webkit-scrollbar": {
                height: 8,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: 4,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c1c1c1",
                borderRadius: 4,
                "&:hover": {
                  backgroundColor: "#a8a8a8",
                },
              },
            }}
          >
            <Table sx={{ minWidth: 1400 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell sx={{ minWidth: 150, whiteSpace: "nowrap" }}>Customer</TableCell>
                  <TableCell sx={{ minWidth: 150, whiteSpace: "nowrap" }}>Order Code</TableCell>
                  <TableCell sx={{ minWidth: 150, whiteSpace: "nowrap" }}>Date</TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>Product Type</TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>Payment Method</TableCell>
                  <TableCell align="right" sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Amount Paid
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                    Outstanding
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, whiteSpace: "nowrap" }}>Status</TableCell>
                  <TableCell align="center" sx={{ minWidth: 200, whiteSpace: "nowrap" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading.pending ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredPending.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {searchQuery ? "No transactions found matching your search" : "No pending transactions found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <React.Fragment>
                    {filteredPending.map((transaction) => (
                      <TableRow
                        key={transaction.order_id || transaction.customer_id}
                        sx={{
                          "&:nth-of-type(even)": { bgcolor: "grey.50" },
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        {/* Customer Column */}
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Avatar
                              sx={{ bgcolor: "#e3f2fd", width: 40, height: 40, mr: 2 }}
                            >
                              <Person sx={{ fontSize: 20, color: "#1976d2" }} />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="500">
                                {transaction.member_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {transaction.customer_email || "No email"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {transaction.customer_phone}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Order Code */}
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography variant="body2" sx={{ fontWeight: "bold", color: "primary.main" }}>
                            {transaction.order_code || "N/A"}
                          </Typography>
                        </TableCell>

                        {/* Date */}
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography variant="body2">
                            {transaction.order_date ? formatDate(transaction.order_date) : "N/A"}
                          </Typography>
                        </TableCell>

                        {/* Product Type */}
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Chip
                            label={transaction.product_id_type || "N/A"}
                            size="small"
                            color={
                              transaction.product_id_type === "GiftCard"
                                ? "primary"
                                : transaction.product_id_type === "Crypto"
                                ? "secondary"
                                : "default"
                            }
                          />
                        </TableCell>

                        {/* Payment Method */}
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Chip
                            label={transaction.mode_of_payment || "N/A"}
                            size="small"
                            color="secondary"
                          />
                        </TableCell>

                        {/* Amount Paid */}
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            {formatCurrency(transaction.amount_paid || "0")}
                          </Typography>
                        </TableCell>

                        {/* Outstanding */}
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: "bold",
                              color: getOutstandingColor(Number(transaction.balance_due || 0)),
                            }}
                          >
                            {formatCurrencyN(Number(transaction.balance_due))}
                          </Typography>
                        </TableCell>

                        {/* Status */}
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Chip
                              label={transaction.order_status || "PENDING"}
                              size="small"
                              color={getOrderStatusColor(transaction.order_status || "PENDING") as any}
                              sx={{ width: "fit-content" }}
                            />
                            {transaction.is_verified === 1 && (
                              <Chip
                                label="Verified"
                                size="small"
                                color="success"
                                sx={{ width: "fit-content" }}
                              />
                            )}
                          </Box>
                        </TableCell>

                        {/* Actions - BOTH BUTTONS */}
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            {/* Process Button */}
                            <Tooltip title="Process Customer Transactions">
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ArrowForward />}
                                onClick={() => handleCustomerSelect(transaction)}
                              >
                                Process
                              </Button>
                            </Tooltip>

                            {/* Verify/View Button */}
                            <Button
                              variant="contained"
                              size="small"
                              color={Number(transaction.is_verified) === 1 ? "success" : "warning"}
                              startIcon={<Visibility />}
                              onClick={() => handleVerifyClick(transaction)}
                            >
                              {Number(transaction.is_verified) === 1 ? "View" : "View"}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Footer with Totals */}
                    <TableRow sx={{ backgroundColor: "grey.200" }}>
                      <TableCell colSpan={5} align="right">
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          Totals
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {formatCurrency(
                            filteredPending.reduce(
                              (sum, tx) => sum + Number(tx.amount_paid || 0),
                              0
                            )
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {formatCurrencyN(totalOutstanding)}
                        </Typography>
                      </TableCell>
                      <TableCell colSpan={2}></TableCell>
                    </TableRow>
                  </React.Fragment>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* ENHANCED VERIFICATION DIALOG - MATCHING FIRST COMPONENT */}
        <Dialog
          open={verifyDialogOpen}
          onClose={() => setVerifyDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle
            sx={{
              bgcolor: "primary.main",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CheckCircle sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                View Transaction 
              </Typography>
            </Box>
            <IconButton
              onClick={() => setVerifyDialogOpen(false)}
              sx={{ color: "white" }}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 0 }}>
            {verifyDialogLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 300,
                }}
              >
                <CircularProgress size={60} />
              </Box>
            ) : transactionToVerify ? (
              <Box sx={{ p: 3 }}>
                {/* Header Summary - MATCHING FIRST COMPONENT */}
                <Card sx={{ mb: 3, bgcolor: "success.light", color: "white" }}>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          {transactionToVerify.product_id_type} -{" "}
                          {transactionToVerify.product_id_type === "GiftCard"
                            ? transactionToVerify.giftcardType || "GiftCard"
                            : transactionToVerify.cryptoType} Transaction
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(transactionToVerify.order_date || "")}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Customer: {transactionToVerify.customer_name || transactionToVerify.member_name || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6} sx={{ textAlign: { md: "right" } }}>
                        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                         {formatCurrency(transactionToVerify.sellingPrice || "0")}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: { md: "flex-end" },
                            gap: 1,
                            mt: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Chip
                            label={transactionToVerify.active_status || transactionToVerify.order_status || "PENDING"}
                            color={
                              (transactionToVerify.active_status || transactionToVerify.order_status) === "COMPLETED"
                                ? "success"
                                : "warning"
                            }
                            sx={{ fontWeight: "bold" }}
                          />
                          <Chip
                            label={
                              transactionToVerify.is_verified === 1
                                ? "VERIFIED"
                                : "PENDING VERIFICATION"
                            }
                            color={transactionToVerify.is_verified === 1 ? "success" : "warning"}
                            sx={{ fontWeight: "bold" }}
                          />
                          <Chip
                            label={
                              transactionToVerify.is_audited === 1
                                ? "AUDITED"
                                : "PENDING AUDIT"
                            }
                            color={transactionToVerify.is_audited === 1 ? "success" : "primary"}
                            sx={{ fontWeight: "bold" }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Grid container spacing={3}>
                  {/* Order Information - Detailed */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Receipt sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Order Information
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Order Code</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{transactionToVerify.order_code || "N/A"}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Order ID</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{transactionToVerify.order_id || "N/A"}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Date & Time</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{formatDate(transactionToVerify.order_date || "")}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Active Status</Typography>
                            <Box sx={{ textAlign: 'right', flex: 1 }}>
                              <Chip label={transactionToVerify.active_status || transactionToVerify.order_status || "PENDING"} color={(transactionToVerify.active_status || transactionToVerify.order_status) === "COMPLETED" ? "success" : "warning"} size="small" />
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Staff Name</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{transactionToVerify.staff_name || "N/A"}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Staff ID</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{transactionToVerify.order_by || "N/A"}</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Payment Information - Detailed */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <AttachMoney sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Payment Information
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          {/* Payment Method */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Payment Method</Typography>
                            <Box sx={{ textAlign: 'right', flex: 1 }}>
                              <Chip label={transactionToVerify.mode_of_payment_real || transactionToVerify.mode_of_payment || "N/A"} color="secondary" size="small" />
                            </Box>
                          </Box>

                          {/* Conditional Amount Fields */}
                          {transactionToVerify?.product_id_type === "GiftCard" && (
                            <> 
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Card Amount</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main', textAlign: 'right', flex: 1 }}>
                                  {Number(transactionToVerify.card_amount || 0).toLocaleString()}
                                </Typography>
                              </Box>

                              {/* RMB Value */}
                              {transactionToVerify.processor_category === "CHINESE" && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>RMB Value</Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 'bold',
                                      color:
                                        Number.parseFloat(transactionToVerify.rmbValue || "0") > 0
                                          ? 'success.main'
                                          : 'error.main',
                                      textAlign: 'right',
                                      flex: 1
                                    }}
                                  >
                                    {
                                      Number(transactionToVerify.rmbValue || 0) *
                                      Number(transactionToVerify.cardUnit || 0)
                                    }
                                  </Typography>
                                </Box>
                              )}
                            </>
                          )}

                          {transactionToVerify?.product_id_type === "Crypto" && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Crypto Amount</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main', textAlign: 'right', flex: 1 }}>
                                {transactionToVerify.cryptoType} {Number(transactionToVerify.cryptoAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Box>
                          )}

                          {transactionToVerify?.crypto_value && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Crypto Value</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main', textAlign: 'right', flex: 1 }}>
                                {(transactionToVerify.crypto_value)}
                              </Typography>
                            </Box>
                          )}

                          {/* Cost Price */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Cost Price</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: Number.parseFloat(transactionToVerify.balance_due || "0") > 0 ? 'error.main' : 'success.main', textAlign: 'right', flex: 1 }}>
                              {formatCurrency(transactionToVerify.costPrice || "0")}
                            </Typography>
                          </Box>

                          {/* Selling Price */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Selling Price</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: Number.parseFloat(transactionToVerify.balance_due || "0") > 0 ? 'error.main' : 'success.main', textAlign: 'right', flex: 1 }}>
                              {formatCurrency(transactionToVerify.sellingPrice || "0")}
                            </Typography>
                          </Box>

                          {/* Commission */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Commission</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: Number.parseFloat(transactionToVerify.commission || "0") > 0 ? 'success.main' : 'error.main', textAlign: 'right', flex: 1 }}>
                              {formatCurrency(transactionToVerify.commission || "0")}
                            </Typography>
                          </Box>

                          {/* Payment Status */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Payment Status</Typography>
                            <Box sx={{ textAlign: 'right', flex: 1 }}>
                              <Chip label={transactionToVerify.payment_status === 1 ? "PAID" : "PENDING"} color={transactionToVerify.payment_status === 1 ? "success" : "warning"} size="small" />
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Customer Information - Detailed */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Person sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Customer Information
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Customer Name</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{transactionToVerify.customer_name || transactionToVerify.member_name || "N/A"}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Customer Phone</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{transactionToVerify.customer_phone || "N/A"}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Customer Email</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{transactionToVerify.customer_email || "N/A"}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Account Name</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{transactionToVerify.customer_account_name || "N/A"}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Account Number</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{transactionToVerify.customer_account_number || "N/A"}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Bank Name</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{transactionToVerify.bank_name || "N/A"}</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Transaction Details - Detailed */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Receipt sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Transaction Details
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Created At</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{formatDate(transactionToVerify.created_at || "")}</Typography>
                          </Box>

                          {/* Conditional: GiftCard vs Crypto */}
                          {transactionToVerify.product_id_type === "GiftCard" && (
                            <> 
                              {transactionToVerify.processor_category === "CHINESE" && (
                                <>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>RMB Value</Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 'bold',
                                        color:
                                          Number.parseFloat(transactionToVerify.rmbValue || "0") > 0
                                            ? 'success.main'
                                            : 'error.main',
                                        textAlign: 'right',
                                        flex: 1
                                      }}
                                    >
                                      {Number(transactionToVerify.rmbValue || 0) *
                                        Number(transactionToVerify.cardUnit || 0)}
                                    </Typography>
                                  </Box>

                                  {/* Naira Rate */}
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Naira Rate</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{formatCurrency(transactionToVerify.naira_rate || "0")}</Typography>
                                  </Box>
                                </>
                              )}
                              
                              {/* Processor / Type */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Processor / Type / Category</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>
                                  {transactionToVerify.external_processor || "N/A"} / {transactionToVerify.processor_type || "N/A"} / {transactionToVerify.processor_category || "N/A"}
                                </Typography>
                              </Box>

                              {/* Load Type */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Load Type</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{transactionToVerify.load_type || "N/A"}</Typography>
                              </Box>

                              {/* Card Rate */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Card Rate</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{transactionToVerify.card_rate || "N/A"}</Typography>
                              </Box>
                            </>
                          )}

                          {transactionToVerify.product_id_type === "Crypto" && (
                            <>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Crypto Type</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{transactionToVerify.cryptoType || "N/A"}</Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Crypto Amount</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{Number(transactionToVerify.cryptoAmount || 0).toFixed(2)}</Typography>
                              </Box>
                            </>
                          )}

                          {/* Last Updated */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Last Updated</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', flex: 1 }}>{formatDate(transactionToVerify.last_updated || "")}</Typography>
                          </Box>

                          {/* Audit Status */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>Audit Status</Typography>
                            <Box sx={{ textAlign: 'right', flex: 1 }}>
                              <Chip label={transactionToVerify.is_audited === 1 ? "AUDITED" : "PENDING AUDIT"} color={transactionToVerify.is_audited === 1 ? "success" : "warning"} size="small" />
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Proof of Payment Section - Detailed */}
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center' }}>
                          <Receipt sx={{ mr: 1, color: 'primary.main' }} />
                          Document Proofs
                        </Typography>
                        
                        <Grid container spacing={3}>
                          {/* Proof of Payment */}
                          <Grid item xs={12} md={6}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
                                Proof of Payment
                              </Typography>
                              <Box sx={{ border: '2px solid', borderColor: 'divider', borderRadius: 2, p: 2, display: 'inline-block' }}>
                                <img
                                  height="200"
                                  width="200"
                                  alt="Proof of Payment"
                                  src={`${baseUrl()}assets/img/gift_card/${transactionToVerify.proof_of_payment || "zillyuser.jpg"}`}
                                  style={{ 
                                    objectFit: 'cover', 
                                    borderRadius: 8,
                                    border: '1px solid #e0e0e0'
                                  }}
                                />
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Payment confirmation document
                              </Typography>
                            </Box>
                          </Grid>

                          {/* Proof of Job Start */}
                          <Grid item xs={12} md={6}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
                                Proof of Job Start
                              </Typography>
                              <Box sx={{ border: '2px solid', borderColor: 'divider', borderRadius: 2, p: 2, display: 'inline-block' }}>
                                <img
                                  height="200"
                                  width="200"
                                  alt="Proof of Job Start"
                                  src={`${baseUrl()}assets/img/gift_card_crypto_start/${transactionToVerify.proof_of_jobstart || "zillyuser.jpg"}`}
                                  style={{ 
                                    objectFit: 'cover', 
                                    borderRadius: 8,
                                    border: '1px solid #e0e0e0'
                                  }}
                                />
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Transaction initiation proof
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Financial Summary - Detailed */}
                  <Grid item xs={12}>
                    <Card sx={{ bgcolor: 'grey.50' }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                          <AttachMoney sx={{ mr: 1 }} />
                          Financial Summary
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} md={3}>
                            <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'white', boxShadow: 2 }}>
                              <CardContent sx={{ p: '0 !important' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, color: 'info.main' }}><AttachMoney /></Box>
                                {transactionToVerify?.product_id_type === "GiftCard" ? (
                                  <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: "medium" }}>Total Card Amount</Typography>
                                    <Typography
                                      variant="h6"
                                      sx={{ fontWeight: "bold", color: "info.main" }}
                                    >
                                      {Number(transactionToVerify.card_amount || 0 * (transactionToVerify.cardUnit || 0)).toLocaleString()}
                                    </Typography>
                                  </>
                                ) : transactionToVerify?.product_id_type === "Crypto" ? (
                                  <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: "medium" }}>Crypto Amount</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "info.main" }}>
                                      {transactionToVerify.cryptoType} {Number(transactionToVerify.cryptoAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                  </>
                                ) : null}
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'white', boxShadow: 2 }}>
                              <CardContent sx={{ p: '0 !important' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, color: 'primary.main' }}><Receipt /></Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>Cost Price</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatCurrency(transactionToVerify.costPrice || "0")}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'white', boxShadow: 2 }}>
                              <CardContent sx={{ p: '0 !important' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, color: 'success.main' }}><CheckCircle /></Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>Selling Price</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>{formatCurrency(transactionToVerify.sellingPrice || "0")}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'white', boxShadow: 2 }}>
                              <CardContent sx={{ p: '0 !important' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, color: Number.parseFloat(transactionToVerify.commission || "0") > 0 ? 'success.main' : 'success.main' }}><Warning /></Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>Commission</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: Number.parseFloat(transactionToVerify.commission || "0") > 0 ? 'success.main' : 'success.main' }}>{formatCurrency(transactionToVerify.commission || "0")}</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            ) : null}
          </DialogContent>
          
          <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Button 
              onClick={() => setVerifyDialogOpen(false)} 
              variant="outlined"
              startIcon={<Close />}
              sx={{ minWidth: 100 }}
            >
              Cancel
            </Button>
            {/* <Button
              onClick={handleVerifyTransaction}
              variant="contained"
              color="primary"
              disabled={isVerifying || verifyDialogLoading || transactionToVerify?.is_verified === 1}
              startIcon={isVerifying ? <CircularProgress size={16} /> : <CheckCircle />}
              sx={{ minWidth: 140 }}
            >
              {isVerifying ? "Verifying..." : transactionToVerify?.is_verified === 1 ? "Already Verified" : "Verify Transaction"}
            </Button> */}
          </DialogActions>
        </Dialog>

        {/* Image Preview Dialog */}
        <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>
            Image Preview
            <IconButton
              sx={{ position: "absolute", right: 8, top: 8 }}
              onClick={() => setImageDialogOpen(false)}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
              }}
            >
              <img
                src={`${baseUrl()}${selectedImage}`}
                alt="Transaction proof"
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  borderRadius: 8,
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImageDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </IonContent>
    </IonPage>
  );
};

export default CustomerAccountPage_giftcard_crypto_pending_om;